<script setup>
import { decode } from "html-entities";
import { load } from "cheerio";
import he from "he";
import {
	extractStockSymbol,
	processHeadingsForReferencedStocks,
	STOCK_TICKER_REGEX
} from "~/utils/referenced-stocks-content";
import {
	BREAKING_NEWS_COUNT,
	pickNewsRecommendations,
	prepareNewsRecommendationsPool,
	RECOMMENDATIONS_POOL_LIMIT,
	RELATED_NEWS_COUNT,
	TRENDING_STORIES_COUNT
} from "~/utils/news-recommendations";
import { useStocksStore } from "~/stores/stocks";
import {
	extractReferencedCompanies,
	ORGANIZATION_ID,
	toCorporationNode
} from "~/utils/organization-schema";

const hasTwitterEmbedd = ref(false);
const showBeyondWords = ref(false);
const content = ref();
const $config = useRuntimeConfig();
const route = useRoute();
const category = ref(null);
const comments = ref([]);
const tags = ref(null);
const commentsCount = ref(null);
const stockSymbols = ref([]);
const adPlacement = ref();
const inlineBreakingVisible = ref(false);
let breakingNewsObserver = null;

const { current_hostname } = useRuntimeConfig().public;
const stocksStore = useStocksStore();

store.toggleLoading();

const stocks_info = {
	stocks: computed(() => stocksStore.stocks)
};

watch(
	stockSymbols,
	async (newSymbols) => {
		if (process.client) {
			if (!newSymbols || newSymbols.length === 0) {
				stocksStore.stopTrackingSymbols();
				return;
			}

			const symbols = newSymbols
				.filter(Boolean)
				.map((symbol) => symbol.toUpperCase());

			await stocksStore.startTrackingSymbols(symbols);
		}
	},
	{
		deep: true,
		immediate: true
	}
);

const stocks = computed(() => stocks_info.stocks.value);

const schemaContentParser = (content) => {
	const textContent = content.replace(/<[^>]*>/g, "");
	return { textContent };
};

/**
 * Normalize date strings for Schema.org format.
 * Schema.org DateTime pattern: [-]CCYY-MM-DDThh:mm:ss[Z|(+|-)hh:mm]
 * This ensures we don't have both +00:00 AND Z suffix which is invalid.
 */
const normalizeSchemaDate = (dateString) => {
	if (!dateString) return dateString;

	// If already ends with Z, return as-is
	if (dateString.endsWith("Z")) {
		return dateString;
	}

	// If has timezone offset like +00:00, return as-is (valid format)
	if (/[+-]\d{2}:\d{2}$/.test(dateString)) {
		return dateString;
	}

	// Otherwise, append Z to indicate UTC
	return `${dateString}Z`;
};

const schemaMaker = (post, current_hostname, _, authors) => {
	const payload = [];

	let author = authors.find(
		(a) =>
			a.name === post.author.name ||
			(a.name === "Tim Sykes" && post.author.name === "Timothy Sykes")
	);

	if (!author) {
		author = authors.find((a) => a.id === 1);
	}
	if (!author) {
		author = {
			name: post.author?.name || "Timothy Sykes",
			profile_image: post.author?.avatar,
			custom_sections: []
		};
	} else {
		author.custom_sections = isValidJSON(author.custom_sections)
			? JSON.parse(author.custom_sections)
			: author.custom_sections || [];
	}
	const reviewers = [];

	const reviewed_by = authors.find(
		(author) =>
			author.name === post.reviewed_by ||
			(author.name === "Tim Sykes" && post.reviewed_by === "Timothy Sykes")
	);

	const fact_checked_by = authors.find(
		(author) =>
			author.name === post.fact_checked_by ||
			(author.name === "Tim Sykes" && post.fact_checked_by === "Timothy Sykes")
	);

	if (reviewed_by) {
		reviewed_by.custom_sections = isValidJSON(reviewed_by.custom_sections)
			? JSON.parse(reviewed_by.custom_sections)
			: reviewed_by.custom_sections;

		reviewed_by.social_links = reviewed_by.custom_sections
			.find((section) => section.name === "Social")
			?.values.map((item) => item.value);

		reviewers.push({
			"@type": "Person",
			name: reviewed_by.name,
			url:
				current_hostname +
				"blog/author/" +
				reviewed_by.name.replace(/ /g, "-").toLowerCase() +
				"/",
			sameAs:
				reviewed_by.social_links?.find(
					(item) =>
						item.includes("twitter") ||
						item.includes("Twitter") ||
						item.includes("x") ||
						item.includes("X")
				) ?? ""
		});
	}

	if (fact_checked_by) {
		fact_checked_by.custom_sections = isValidJSON(fact_checked_by.custom_sections)
			? JSON.parse(fact_checked_by.custom_sections)
			: fact_checked_by.custom_sections;

		fact_checked_by.social_links = fact_checked_by.custom_sections
			.find((section) => section.name === "Social")
			?.values.map((item) => item.value);

		reviewers.push({
			"@type": "Person",
			name: fact_checked_by.name,
			url:
				current_hostname +
				"blog/author/" +
				fact_checked_by.name.replace(/ /g, "-").toLowerCase() +
				"/",
			sameAs:
				fact_checked_by.social_links.find(
					(item) =>
						item.includes("twitter") ||
						item.includes("Twitter") ||
						item.includes("x") ||
						item.includes("X")
				) ?? ""
		});
	}

	const social_links = author.custom_sections
		.find((section) => section.name === "Social")
		?.values.map((item) => item.value);

	const post_modified_gmt = [
		post.modified_gmt,
		post.modified
	].find((v) => v && typeof v === "string");

	const post_date_gmt = [post.date_gmt, post.date].find(
		(v) => v && typeof v === "string"
	);

	const article = {
		"@context": "https://schema.org",
		"@type": "NewsArticle",
		headline: post.title,
		alternativeHeadline: post.title,
		image: [],
		datePublished: normalizeSchemaDate(post_date_gmt),
		dateModified: normalizeSchemaDate(post_modified_gmt),
		identifier: current_hostname + "news/" + post.slug + "/#Article",
		url: current_hostname + "news/" + post.slug + "/",
		description: schemaContentParser(post.excerpt).textContent,
		disambiguatingDescription: schemaContentParser(post.excerpt).textContent,
		articleBody: "",
		articleSection: "Finance",
		inLanguage: "en",
		isAccessibleForFree: true,
		author: {
			"@type": "Person",
			name: author?.name || "Timothy Sykes",
			url: `https://www.timothysykes.com/blog/author/${
				author.name ? author.name.toLowerCase().replace(/ /g, "-") : "tim-sykes"
			}/`,
			sameAs: social_links || [
				"https://www.facebook.com/timsykesfans/",
				"https://twitter.com/timothysykes",
				"https://www.linkedin.com/in/timothysykes/",
				"https://www.instagram.com/timothysykes/",
				"https://www.youtube.com/channel/UCoSG43KFjTe0trsteSQ46Ng"
			],
			image: [
				{
					"@type": "ImageObject",
					url:
						author.profile_image ||
						"https://content.timothysykes.com/wp-content/uploads/2024/08/timsykes.webp",
					width: 306,
					height: 384
				},
				{
					"@type": "ImageObject",
					url:
						author.profile_image ||
						"https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g",
					height: 96,
					width: 96
				}
			],
			jobTitle: "Penny Stock Trader and Mentor",
			publishingPrinciples: "https://www.timothysykes.com/editorial-guidelines/",
			affiliation: {
				"@type": "Organization",
				"@id": ORGANIZATION_ID,
				name: "Timothy Sykes",
				sameAs: "https://www.timothysykes.com"
			}
		},
		publisher: {
			"@type": "Organization",
			"@id": ORGANIZATION_ID,
			name: "Timothy Sykes",
			url: "https://www.timothysykes.com",
			logo: {
				"@type": "ImageObject",
				url: current_hostname + "images/ts-logo-icon.png",
				width: 512,
				height: 512
			},
			image: {
				"@type": "ImageObject",
				url: current_hostname + "images/ts-logo-white-bg.png",
				width: 503,
				height: 95
			},
			publishingPrinciples: "https://www.timothysykes.com/editorial-guidelines/",
			sameAs: [
				"https://www.facebook.com/timsykesfans/",
				"https://twitter.com/timothysykes",
				"https://www.linkedin.com/in/timothysykes/",
				"https://www.instagram.com/timothysykes/",
				"https://www.youtube.com/channel/UCoSG43KFjTe0trsteSQ46Ng"
			]
		},
		keywords: "",
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": current_hostname + "news/" + post.slug + "/",
			reviewedBy: reviewers
		}
	};

	const featuredImage = {
		"@type": "ImageObject",
		"@id": current_hostname + "news/" + post.slug + "/#primaryimage",
		url:
			post._embedded["wp:featuredmedia"] && post._embedded["wp:featuredmedia"][0]
				? post._embedded["wp:featuredmedia"][0].source_url
				: "",
		width:
			post._embedded["wp:featuredmedia"] &&
			post._embedded["wp:featuredmedia"][0] &&
			post._embedded["wp:featuredmedia"][0].media_details &&
			post._embedded["wp:featuredmedia"][0].media_details.width
				? post._embedded["wp:featuredmedia"][0].media_details.width
				: "",
		height:
			post._embedded["wp:featuredmedia"] &&
			post._embedded["wp:featuredmedia"][0] &&
			post._embedded["wp:featuredmedia"][0].media_details &&
			post._embedded["wp:featuredmedia"][0].media_details.height
				? post._embedded["wp:featuredmedia"][0].media_details.height
				: ""
	};

	if (
		post._embedded["wp:featuredmedia"] &&
		post._embedded["wp:featuredmedia"][0]
	) {
		article.image = [featuredImage];
	}

	const [primaryCompany, ...otherCompanies] = extractReferencedCompanies(
		post.content
	).map(toCorporationNode);

	if (primaryCompany) {
		article.about = primaryCompany;
	}
	if (otherCompanies.length > 0) {
		article.mentions = otherCompanies;
	}

	payload.push({ "@type": "NewsArticle", ...article });

	return [
		{
			"@context": "https://schema.org"
		},
		...payload
	];
};

const fetchLeftoverInformation = async () => {
	try {
		const { data } = await $fetch($config.public.hostname + "graphql", {
			method: "post",
			body: {
				query: `
            query NewQuery {
              postBy(slug: "${route.path}") {
				${POST_CATEGORIES_QUERY}
                ${TAGS_QUERY}
                ${COMMENTS_QUERY}
              }
            }`
			}
		});

		if (data && data.postBy) {
			category.value = data.postBy.categories.nodes[0];
			comments.value = data.postBy.comments.nodes;
			tags.value = data.postBy.tags.nodes;
			commentsCount.value = data.postBy.commentsCount;
		}
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
	}
};

// Use unique key per route for proper SSR hydration
const routeSlug = route.params.slug?.[0] || "index";
const { data, error: fetchError } = await useAsyncData(
	`news_post_data_${routeSlug}`,
	async () => {
		try {
			const params = route.params;
			const routePath = route.path;

			const payload = {
				post: null,
				beyondwords: null,
				schema_json: null,
				authorship: null,
				faq: null,
				news_list: []
			};

			if (
				routePath.startsWith("/.well-known") ||
				routePath.includes("favicon.ico")
			) {
				return payload;
			}

			const SLUG = params.slug[0];

			// Per-slug payload comes straight from S3 (ts-posts/news/<slug>.json)
			// via /api/dictionary/news. The S3 file is the FULL article — content,
			// yoast_head_json, pre-resolved author_info, tags and categories. No
			// WordPress REST round-trip is needed.
			const news_data = await $fetch("/api/dictionary/news", {
				params: {
					slug: SLUG
				}
			});

			// Handle redirect for slugs with dots - redirect to sanitized (hyphen) URL
			if (news_data?.redirect) {
				return navigateTo(news_data.redirectUrl, {
					redirectCode: 301,
					external: false
				});
			}

			if (!news_data || !news_data.id) {
				throw createError({
					statusCode: 404,
					statusMessage: "No News story found",
					fatal: true
				});
			}

			const news_recommendations = await $fetch("/api/dictionary/news", {
				params: {
					recommendations: true,
					limit: RECOMMENDATIONS_POOL_LIMIT
				}
			});

			payload.news_list = prepareNewsRecommendationsPool(
				news_recommendations,
				SLUG
			);

			const response = normalizeS3PostToWpShape(news_data);

			// Authors lookup is still needed so schemaMaker can resolve
			// reviewed_by / fact_checked_by social links — the post's own author
			// is already pre-resolved on the S3 payload as `author_info`.
			const all_authors = await $fetch("/api/authors").catch((e) => {
				console.warn("[news SSR] authors fetch failed:", e?.message);
				return [];
			});

			payload.post = response;

			const fetched_author = response.author_info ||
				all_authors.find((a) => a.id === 1) || {
					id: response.author,
					name: "Timothy Sykes",
					profile_image: ""
				};

			if (response.reviewed_by) {
				payload.authorship = {
					...payload.authorship,
					reviewer: { name: response.reviewed_by }
				};
			}

			if (response.fact_checked_by) {
				payload.authorship = {
					...payload.authorship,
					factChecker: { name: response.fact_checked_by }
				};
			}

			if (response.meta && response.meta.beyondwords_podcast_id) {
				const beyondwords_res = await fetchBeyondWords({
					podcastId: response.meta.beyondwords_podcast_id
				});

				if (beyondwords_res) {
					payload.beyondwords = beyondwords_res;
				}
			}

			let rating;

			payload.post = {
				...payload.post,
				author: {
					id: fetched_author.id,
					name: fetched_author?.name,
					avatar: fetched_author?.profile_image
				}
			};

			const firstCategoryId =
				Array.isArray(response.categories) && response.categories.length > 0
					? typeof response.categories[0] === "object"
						? response.categories[0].id
						: response.categories[0]
					: null;

			if ([118, 3].includes(firstCategoryId)) {
				rating = await getPostRatings(
					response.id,
					$config.public.ratings_url,
					$config.public.ratings_key
				);
			}

			const schema = schemaMaker(
				payload.post,
				$config.public.current_hostname,
				rating,
				all_authors
			);

			if (schema) {
				schema.forEach((item) => {
					if (item["@type"] === "BreadcrumbList") {
						item.itemListElement.forEach((element) => {
							if (element.item && element.item.reviewedBy) {
								delete element.item.reviewedBy;
							}
						});
					}
				});
			}
			payload.schema_json = JSON.stringify([
				...schema,
				{
					"@type": "BreadcrumbList",
					"@id":
						$config.public.current_hostname +
						"news/" +
						response.slug +
						"/#breadcrumb",
					"@context": "https://schema.org",
					itemListElement: [
						{
							"@type": "ListItem",
							position: 1,
							item: {
								"@id": $config.public.current_hostname,
								name: "Timothy Sykes"
							}
						},
						{
							"@type": "ListItem",
							position: 2,
							item: {
								"@id": $config.public.current_hostname + "category/news/",
								name: "Stock News"
							}
						},
						{
							"@type": "ListItem",
							position: 3,
							item: {
								"@id": $config.public.current_hostname + "news/" + response.slug + "/",
								name: response.title
							}
						}
					]
				}
			]);

			if (payload.post.post_template === "B") {
				const $ = load(payload.post.content, {
					decodeEntities: false,
					xmlMode: false
				});

				const important_points = $("ul:not(.toc)").first();

				if (important_points.length > 0) {
					const points = important_points.find("li");
					const keypoints = points.get().map((el) => $(el).text().trim());
					const title = important_points.prev().text().trim();
					payload.post.important_points = { title, keypoints };
				}
			}

			return { ...payload };
		} catch (error) {
			console.error("Error fetching news data:", error);

			throw createError({
				statusCode: error.statusCode || 500,
				statusMessage:
					error.statusMessage || error.message || "An error occurred during fetch",
				fatal: true
			});
		}
	},
	{
		watch: [() => route.path],
		getCachedData(key, nuxtApp) {
			const cached = nuxtApp.payload.data[key] ?? nuxtApp.static?.data?.[key];
			if (cached) return cached;

			// During hydration, payload.data may be empty. Fall back to useState (in payload.state).
			if (nuxtApp.isHydrating && process.client) {
				const slug = route.params.slug?.[0] || "index";
				const stateKey = `$sssr_news_${slug}`;
				const ssrState = nuxtApp.payload?.state?.[stateKey];
				if (ssrState) return ssrState;
			}

			return undefined;
		}
	}
);

// If useAsyncData captured an error (e.g. 404 from API), throw it at the script setup
// level so Nuxt's error handler triggers error.vue with the correct HTTP status code.
if (fetchError.value) {
	throw createError({
		statusCode: fetchError.value.statusCode || 404,
		statusMessage: fetchError.value.statusMessage || "Page Not Found",
		fatal: true
	});
}

// Bridge SSR data to client via useState (payload.data is empty during hydration,
// but payload.state used by useState IS available — confirmed via debug logs)
const ssrData = useState(`ssr_news_${routeSlug}`, () => data.value ?? null);

// Keep ssrData updated when useAsyncData resolves on client
watch(
	() => data.value,
	(newData) => {
		if (newData) ssrData.value = newData;
	},
	{ immediate: false }
);

// Use computed properties with useState fallback for hydration
const post = computed(() => data.value?.post ?? ssrData.value?.post ?? null);
const beyondwords = computed(
	() => data.value?.beyondwords ?? ssrData.value?.beyondwords ?? null
);
const authorship = computed(
	() => data.value?.authorship ?? ssrData.value?.authorship ?? null
);
const faq = computed(() => data.value?.faq ?? ssrData.value?.faq ?? null);
const news_list = computed(
	() => data.value?.news_list ?? ssrData.value?.news_list ?? []
);

const currentArticleSlug = computed(
	() => post.value?.slug ?? route.params.slug?.[0] ?? ""
);

const filteredRecommendationsPool = computed(() => news_list.value || []);

const breakingNewsItems = computed(() =>
	filteredRecommendationsPool.value.slice(0, BREAKING_NEWS_COUNT)
);

const trendingStories = computed(() => {
	const pool = filteredRecommendationsPool.value;
	if (!pool.length) return [];

	if (inlineBreakingVisible.value) {
		return pickNewsRecommendations(pool, {
			excludeSlugs: breakingNewsItems.value.map((item) => item.slug),
			limit: TRENDING_STORIES_COUNT
		});
	}

	return pool.slice(0, TRENDING_STORIES_COUNT);
});

const relatedNewsItems = computed(() => {
	const excludeSlugs = [
		currentArticleSlug.value,
		...breakingNewsItems.value.map((item) => item.slug),
		...filteredRecommendationsPool.value
			.slice(0, TRENDING_STORIES_COUNT + BREAKING_NEWS_COUNT)
			.map((item) => item.slug)
	];

	return pickNewsRecommendations(filteredRecommendationsPool.value, {
		excludeSlugs,
		limit: RELATED_NEWS_COUNT
	});
});

const teardownBreakingNewsObserver = () => {
	breakingNewsObserver?.disconnect();
	breakingNewsObserver = null;
	inlineBreakingVisible.value = false;
};

const setupBreakingNewsObserver = () => {
	if (!process.client) return;

	teardownBreakingNewsObserver();

	nextTick(() => {
		const block = content.value?.querySelector?.(".more-breaking-news-block");
		if (!block) return;

		breakingNewsObserver = new IntersectionObserver(
			([entry]) => {
				inlineBreakingVisible.value = entry.isIntersecting;
			},
			{ threshold: 0.5 }
		);
		breakingNewsObserver.observe(block);
	});
};

// isLoading based purely on data availability (post has useState fallback for hydration)
// Don't check status.value — during re-fetch after hydration, useState still provides data
const isLoading = computed(() => !post.value);

const lastStockParsePostId = ref(null);

const contentFormatter = (content) => {
	if (!content) return "";

	const postId = post.value?.id;
	if (postId !== lastStockParsePostId.value) {
		stockSymbols.value = [];
		lastStockParsePostId.value = postId;
	}

	const $ = load(content, {
		decodeEntities: false,
		xmlMode: false
	});

	if (breakingNewsItems.value.length > 0) {
		const moreNewsList = `
		<div class="more-breaking-news-block" data-news-widget="breaking">
			<h4 class="mt-12 text-xl font-bold" style="margin-bottom: 24px;">
				More Breaking News
			</h4>
			<ul class="list-disc mb-12">
				${breakingNewsItems.value
					.map(
						(item) =>
							`<li style="margin-bottom: 24px;"> <a href="/news/${item.slug}/"> ${item.title} </a></li>`
					)
					.join("")}
			</ul>
		</div>
	`;

		const h3 = $("h3");
		const h2 = $("h2");
		const paragraphs = $("p");

		if (h3.length > 1) {
			const thirdH3 =
				$("h3").eq(2).text() !== ""
					? $("h3").eq(2)
					: $("h3").eq(3).text() !== ""
					? $("h3").eq(3)
					: $("h2").eq(2);
			const previousElement = thirdH3.prev();

			if (previousElement.is("h2")) {
				previousElement.before(moreNewsList);
			} else {
				thirdH3.before(moreNewsList);
			}
		} else if (h2.length > 1) {
			h2.eq(2).prev().before(moreNewsList);
		} else if (paragraphs.length > 10) {
			const index = Math.floor(paragraphs.length / 2);
			paragraphs.eq(index).after(moreNewsList);
		}
	}

	$("script").each((_index, scriptElement) => {
		if (
			$(scriptElement).attr("src") === "https://platform.twitter.com/widgets.js"
		) {
			$(scriptElement).remove();

			hasTwitterEmbedd.value = true;
		}

		if ($(scriptElement).attr("async")) {
			$(scriptElement).removeAttr("async");

			$(scriptElement).attr("defer", "");
		}
	});

	$("iframe").each((_index, iframeEl) => {
		if (!$(iframeEl).attr("loading")) {
			$(iframeEl).attr("loading", "lazy");
		}
		if (!$(iframeEl).attr("title")) {
			$(iframeEl).attr("title", $(iframeEl).attr("src"));
		}

		if (iframeEl.attribs.src.includes("displayprices=Candlestick")) {
			$(iframeEl).addClass("stockdio-candlestick");
		}
	});

	$("a").each((_index, el) => {
		if (
			el.attribs.href &&
			el.attribs.href.includes("https://content.timothysykes.com")
		) {
			el.attribs.href = el.attribs.href.replace(
				"https://content.timothysykes.com",
				$config.public.current_hostname.slice(0, -1)
			);
		}

		const regex = new RegExp(
			"\\b" + $config.public.hostname + "(?!\\.(jpg|jpeg|png|webp|pdf|gif)\\b)",
			"gi"
		);

		if (el.attribs.href && el.attribs.href.match(regex)) {
			const extracted_slug = el.attribs.href.split(".com/")[1];
			$(el).attr("href", $config.public.current_hostname + extracted_slug);
		}

		if (
			el.attribs.href &&
			!el.attribs.href.includes("#") &&
			$(el).find("img").length === 0
		) {
			$(el).addClass("external-link");
		}
	});

	$("#toc_container .toc_title").each((_index, toc_title) => {
		const button = $(`<button aria-label="Toggle Table of Contents">
      <span class="expand"> <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/><path d="M12.5 7.5V17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/><path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg></span>
      <span class="collapse">
        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/>
          <path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        </svg>

        </span>
      </button>`);

		$(toc_title).append(button);
	});

	$("img").each((_index, imgElement) => {
		const content = $config.public.hostname;
		let src = imgElement.attribs.src;
		let srcset = imgElement.attribs.srcset;

		const pattern = new RegExp($config.public.hostname, "g");
		const proxy_srcset = "" + $config.public.hostname;

		src = src.replace($config.public.hostname, content);

		if (srcset) {
			if (["production", "development"].includes($config.public.env)) {
				srcset = srcset.replace(pattern, proxy_srcset);
			}
			$(imgElement).attr("srcset", srcset ?? "");
		}

		const proxy = "";

		$(imgElement).attr(
			"src",
			["production", "development"].includes($config.public.env)
				? proxy + src
				: src
		);
		$(imgElement).attr(
			"data-src",
			["production", "development"].includes($config.public.env)
				? proxy + src
				: src
		);
	});

	$(".lyMe.hidef .pL").each((_index, ytElement) => {
		const proxy = "";

		const image = `<img src="${proxy}${ytElement.attribs["data-src"]}" alt="${ytElement.attribs["data-src"]}" class="yt-embed-image"  loading="lazy"/>`;

		$(ytElement).removeAttr("style");
		$(ytElement).removeAttr("data-src");
		$(".lyMe.hidef .pL").first().removeAttr("style");
		$(ytElement).append(image);
	});

	processHeadingsForReferencedStocks($, (stockSymbol) => {
		if (!stockSymbols.value.includes(stockSymbol)) {
			stockSymbols.value.push(stockSymbol);
		}
	});

	$("hr").each((_, pElement) => {
		$(pElement).remove();
	});
	$("p").each((_, pElement) => {
		const text = $(pElement).text();

		const matches = text.match(STOCK_TICKER_REGEX);

		if (matches && matches[1]) {
			const stockSymbol = extractStockSymbol(text);
			if (!stockSymbols.value.includes(stockSymbol)) {
				stockSymbols.value.push(stockSymbol);
			}
		}
	});

	$("h1").each((_, h1Element) => {
		const h2Element = $("<h2></h2>");
		h2Element.addClass("page-title");
		h2Element.html($(h1Element).html());
		$(h1Element).replaceWith(h2Element);
	});

	$("table.dynamic-table").each((_, tableElement) => {
		const $table = $(tableElement);
		const $tbody = $table.find("tbody");
		const $tr = $tbody.find("tr");

		$tr.each((_, trElement) => {
			const $td = $(trElement).find("td");
			const regex = /\b(NASDAQ|NYSE|AMEX|OTCPK)\b\s*:\s*([A-Z]+)\b/;
			let stockSymbol = null;

			// Search through all columns to find the ticker
			for (let i = 0; i < $td.length; i++) {
				const text = $td.eq(i).text().trim();
				const matches = text.match(regex);

				if (matches && matches[2]) {
					stockSymbol = matches[2].replace(/\s/g, "").toUpperCase();
					break;
				}
			}

			if (stockSymbol) {
				// Track the ticker so YTD data gets fetched for table-only symbols
				if (!stockSymbols.value.includes(stockSymbol)) {
					stockSymbols.value.push(stockSymbol);
				}

				// Set the data-stock-ticker attribute on the Performance (YTD) column (usually the 3rd column, index 2)
				if ($td.length > 2) {
					$td.eq(2).attr("data-stock-ticker", stockSymbol);
				}
			}
		});
	});

	const firstParagraph = $("p").first();
	if (firstParagraph.length > 0) {
		firstParagraph.after('<div class="mobile-cta-slot"></div>');
	}

	return $.html();
};

onMounted(async () => {
	// Sidebar initialization is handled by the watcher above (supports direct page loads)

	if (process.client) {
		setupBreakingNewsObserver();

		if (post.value?.content) {
			const $ = load(post.value.content, {
				decodeEntities: false,
				xmlMode: false
			});
		}

		await fetchLeftoverInformation();

		window.scrollTo(0, 0);

		if (post.value?.id) {
			store.changePostId(post.value.id);
		}

		if (post.value) {
			store.toggleLoading();
		}

		let counter = 0;

		const hostname = "https://content.timothysykes.com/";

		// Wait for content to load before parsing YouTube embeds
		// 250ms intervals, up to 10 retries (2.5 seconds total) for mobile devices
		const interval = setInterval(() => {
			counter += 1;
			const result = youtubeEmbedParser(hostname);
			if (result === "close" || counter >= 10) {
				clearInterval(interval);
			}
		}, 250);

		await setTimeout(() => {
			showBeyondWords.value = true;
		}, 500);
	}
});

onBeforeUnmount(() => {
	teardownBreakingNewsObserver();
});

watch(
	() => post.value?.slug,
	() => {
		if (process.client) {
			setupBreakingNewsObserver();
		}
	}
);

watch(content, async (newContent) => {
	if (process.client && newContent) {
		setupBreakingNewsObserver();

		const toc_container = await document.querySelector("#toc_container");

		if (toc_container) {
			const button = toc_container.querySelector("button");
			const anchor_links = toc_container.querySelectorAll("li a");

			anchor_links.forEach((link) =>
				link.addEventListener("click", onClickOnAnchorScrollToTitle)
			);

			button.addEventListener("click", () => {
				const toc = toc_container.querySelector(".toc_list");

				if (toc.classList.contains("active")) {
					button.classList.remove("active");
					toc.classList.remove("active");
				} else {
					button.classList.add("active");
					toc.classList.add("active");
				}
			});
		}
	}
});

watch(hasTwitterEmbedd, async (newContent) => {
	if (process.client && newContent) {
		await setTimeout(addTwitterEmbedd, 1000);
	}
});

// eslint-disable-next-line require-await
watch(adPlacement, async (newContent, _) => {
	if (process.client && newContent) {
		if (process.client && newContent) {
			let counter = 0;

			const interval = setInterval(() => {
				const endOfArticleDiv = document.querySelector(".end-of-article");

				if (endOfArticleDiv) {
					clearInterval(interval);

					// Load and parse content using Cheerio
					const $ = load(endOfArticleDiv.innerHTML);
					$("p").each((_, element) => {
						const content = $(element).html();

						if (content.includes('[a href="link"]') && content.includes("[/a]")) {
							$(element).html(
								content.replace(
									/\[a href="([^"]+)"\](.*?)\[\/a\]/g,
									'<a href="$1">$2</a>'
								)
							);
						}
					});
				} else {
					counter += 1;
					if (counter > 20) {
						clearInterval(interval);
					}
				}
			}, 200);
		}
	}
});

watch(
	stocks,
	(newData, _) => {
		if (newData && process.client) {
			Object.entries(newData).forEach(([_, stock]) => {
				const element = document.querySelector(
					`[data-stock-ticker="${stock.SYMBOL}"]`
				);

				if (element && stock.YTD_PCT != null) {
					element.setAttribute(
						"class",
						stock.YTD_PCT > 0 ? "symbol-status up" : "symbol-status down"
					);
					element.innerHTML = `${stock.YTD_PCT > 0 ? "+" : ""}${stock.YTD_PCT}%`;
				}
			});
		}
	},
	{ deep: true }
);

const mainImageFormated = computed(() => {
	if (!post.value) return null;

	const proxy_srcset = "" + $config.public.hostname;
	const proxy_srcset_lowres =
		"https://content.timothysykes.com/cdn-cgi/image/quality=10,format=webp,width=240/" +
		$config.public.hostname;

	if (post.value._embedded?.["wp:featuredmedia"]?.[0]?.source_url) {
		if (!["development", "production"].includes($config.public.env)) {
			return {
				featuredImage: post.value._embedded["wp:featuredmedia"][0].source_url,
				lowRes: post.value._embedded["wp:featuredmedia"][0].source_url
			};
		}

		return {
			featuredImage: post.value._embedded[
				"wp:featuredmedia"
			][0].source_url.replace($config.public.hostname, proxy_srcset),
			lowRes: post.value._embedded["wp:featuredmedia"][0].source_url.replace(
				$config.public.hostname,
				proxy_srcset_lowres
			)
		};
	}

	return null;
});

const fallbackPost = {
	title: "Top 10 Most Successful Penny Stocks in History",
	text: `Everyone wants to know about the most successful penny stocks in history … They want to believe in the dream of getting filthy rich on cheap stocks.
They also want to buy big houses and expensive vehicles without doing all the hard work.`,
	url: "/blog/most-successful-penny-stocks-in-history/",
	image:
		"https://content.timothysykes.com/wp-content/uploads/2020/08/most-successful-penny-stocks-in-history.jpg"
};

useHead(() => {
	const head_obj = {
		script: [
			{
				type: "application/ld+json",
				class: "saswp-schema-markup-output",
				innerHTML: data.value?.schema_json
			}
		],
		meta: [],
		title: null
	};

	// Add SEO meta tags when post data is available
	if (post.value) {
		const rawDescription =
			post.value.yoast_head_json?.og_description ||
			schemaContentParser(post.value.excerpt || "").textContent ||
			"";
		const decodedDescription = he.decode(rawDescription);
		const description = decodedDescription.length > 160
			? decodedDescription.slice(0, 160).replace(/\s+\S*$/, "").trimEnd()
			: decodedDescription;
		const ogDescription = decodedDescription.length > 300
			? decodedDescription.slice(0, 300).replace(/\s+\S*$/, "").trimEnd()
			: decodedDescription;

		const featuredImageUrl =
			post.value._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "";

		const rawDate = post.value.date_gmt || "";
		const publishedTime = rawDate
			? rawDate.endsWith("Z")
				? rawDate
				: rawDate + "Z"
			: "";

		const decodedTitle = he.decode(post.value.title);
		const tickerSuffix =
			stockSymbols.value.length > 0
				? ` (${stockSymbols.value[0].replace(/\s+/g, "").toUpperCase()})`
				: "";
		head_obj.title = `${decodedTitle}${tickerSuffix} | Timothy Sykes`;

		head_obj.meta = [
			getRobotsMeta($config.public.env),
			{ name: "language", content: "en_US" },
			{ name: "title", content: decodedTitle },
			{ name: "description", content: description },
			{ property: "og:title", content: decodedTitle },
			{ property: "og:description", content: ogDescription },
			{ property: "og:type", content: "article" },
			{ property: "og:site_name", content: "StocksToTrade" },
			{
				property: "og:url",
				content: `https://www.timothysykes.com${route.fullPath}`
			},
			{ property: "og:image", content: featuredImageUrl },
			...(publishedTime
				? [{ property: "article:published_time", content: publishedTime }]
				: []),
			...(post.value.author?.name
				? [{ property: "article:author", content: post.value.author.name }]
				: []),
			{ property: "article:section", content: "Finance" },
			{ name: "twitter:card", content: "summary_large_image" },
			{ property: "twitter:title", content: decodedTitle },
			...(featuredImageUrl
				? [{ name: "twitter:image", content: featuredImageUrl }]
				: []),
			{ name: "twitter:label1", content: "Written by" },
			{ name: "twitter:data1", content: post.value.author?.name || "" },
			{ name: "twitter:label2", content: "Est. reading time" },
			{
				name: "twitter:data2",
				content: `${calculateReadingTime(post.value.content || "")} minutes`
			}
		];
	} else {
		head_obj.meta = [getRobotsMeta($config.public.env)];
	}

	return head_obj;
});

// Use useState to maintain consistent year/month between server and client
const currentYear = useState("currentYear", () => new Date().getFullYear());
const currentMonth = useState("currentMonth", () =>
	new Date().toLocaleString("en-US", { month: "long" })
);

const postTitle = computed(() => {
	if (!post.value?.title) return "";

	let title = post.value.title;
	if (title.includes("[current_year]") || title.includes("[current_month]")) {
		title = title
			.replace(/\[current_year\]/g, String(currentYear.value))
			.replace(/\[current_month\]/g, String(currentMonth.value));
		return decode(title);
	}

	return decode(title);
});

const showReferencedStocksSidebar = computed(() => {
	if (!stocks?.value) return false;

	return (
		stockSymbols.value.length > 0 &&
		Object.values(stocks.value).some((stock) => stock?.NAME && stock?.SYMBOL)
	);
});

// Hero date format: "JAN. 21, 2026, 10:04 AM ET"
const formattedHeroDate = computed(() => {
	const dateStr = [
		post.value?.modified_gmt,
		post.value?.date_gmt,
		post.value?.modified,
		post.value?.date
	].find((v) => v && typeof v === "string");

	if (!dateStr) return "";

	// Append Z only when there is no existing timezone designator
	const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(dateStr)
		? dateStr
		: `${dateStr}Z`;
	const date = new Date(normalized);
	if (isNaN(date.getTime())) return "";

	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: "America/New_York",
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true
	}).formatToParts(date);

	const get = (type) => parts.find((p) => p.type === type)?.value ?? "";

	return `${get("month").toUpperCase()}. ${get("day")}, ${get("year")}, ${get(
		"hour"
	)}:${get("minute")} ${get("dayPeriod")} ET`;
});

// Primary stock symbol from article content
const primaryStockSymbol = computed(() => {
	return stockSymbols.value.length > 0 ? stockSymbols.value[0] : null;
});

// Primary stock data for market data bar
const primaryStockData = computed(() => {
	if (!primaryStockSymbol.value || !stocks.value) return null;
	return stocks.value[primaryStockSymbol.value] || null;
});

const watchlistCtaUrl = useCampaignUrl(
	"https://traders.timothysykes.com/after-hours/night-tape?utm_medium=ts.com&utm_source=tim&utm_term=web_ads&utm_campaign=oKfY7g"
);

const renderMobileCtaCard = () => {
	if (!process.client) return;
	const slot = document.querySelector(".mobile-cta-slot");
	if (!slot) return;

	const symbol = primaryStockSymbol.value
		? primaryStockSymbol.value.replace(/\s+/g, "")
		: "TICKER";
	const ctaUrl = watchlistCtaUrl.value;

	slot.innerHTML = `
		<a href="${ctaUrl}" target="_blank" rel="noopener noreferrer" class="news-cta-card news-cta-card--inline-mobile">
			<div class="news-cta-card__content">
				<h3 class="news-cta-card__title news-cta-card__title--inline">
					Click Here for a Millionaire's POV on Trading
					<span class="news-cta-card__ticker">${symbol}</span>
				</h3>
			</div>
			<img src="/images/ts-ad-cutout.webp" alt="Tim Sykes" class="news-cta-card__image" loading="lazy" />
		</a>
	`;
};

watch(
	[content, primaryStockSymbol],
	() => {
		if (process.client) {
			nextTick(() => renderMobileCtaCard());
		}
	},
	{ deep: true }
);

const reviewerName = computed(() => authorship.value?.reviewer?.name ?? null);
const factCheckerName = computed(
	() => authorship.value?.factChecker?.name ?? null
);

// Format date for trending stories: "Jan 21, 2026"
const formatTrendingDate = (dateStr) => {
	if (!dateStr) return "";
	// Strip trailing Z when a timezone offset is already present (e.g. "-05:00Z")
	const cleaned = String(dateStr).replace(/([+-]\d{2}:\d{2})Z$/, "$1");
	const date = new Date(cleaned);
	if (isNaN(date.getTime())) return "";
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC"
	});
};
</script>

<template>
	<Head>
		<Title>
			{{ postTitle }}
		</Title>

		<Link
			rel="preconnect"
			:href="`${current_hostname?.slice(0, -1) || ''}`"
		/>

		<Link
			rel="canonical"
			:href="`${current_hostname?.slice(0, -1) || ''}${route.path}`"
		/>
	</Head>

	<div v-if="post">
		<BlogContainer>
			<template #main>
				<!-- Hero Section -->
				<NewsHero
					:title="post.title"
					:featured-image="mainImageFormated?.featuredImage ?? null"
					:author-name="post.author?.name || 'Timothy Sykes'"
					:formatted-date="formattedHeroDate"
					:reviewer-name="reviewerName"
					:fact-checker-name="factCheckerName"
				/>

				<!-- Market Data Bar -->
				<MarketDataBar
					:primary-stock-symbol="primaryStockSymbol"
					:primary-stock-data="primaryStockData"
				/>

				<LazyBeyondWordsIframe
					v-if="showBeyondWords && beyondwords && beyondwords.id"
					:beyondwords="beyondwords.id"
				/>

				<section
					ref="content"
					class="content"
					v-html="contentFormatter(post.content)"
				/>

				<AizenEndOfArticleAd />

				<template v-if="tags && tags.length">
					<Divider />
					<div class="flex flex-wrap gap-[12px]">
						<img
							src="/images/tags-solid.svg"
							alt="tags-icon"
							height="24"
							width="24"
							class="tags-icon"
						/>
						<template
							v-for="(tag, index) in tags"
							:key="`lk-${index}`"
						>
							<div>
								<NuxtLink
									class="category-tag text-primary-500"
									:to="`/tag/${tag.slug}/`"
								>
									{{ tag.name }}
								</NuxtLink>
								<span
									v-if="index + 1 !== tags.length"
									:key="'sp-' + index"
									class="breadcrumb-caret"
								>
									,&nbsp;
								</span>
							</div>
						</template>
					</div>
				</template>

				<LazyFaqComponent
					v-if="faq"
					class="px-0"
					:route="$route.path"
					:faq="faq"
				/>

				<Divider />

				<RatingsComponent
					id="ratings-component"
					:post-id="post.id"
				/>

				<template v-if="relatedNewsItems.length">
					<Divider />

					<section id="related-posts">
						<Heading
							tag="h4"
							:text="`Keep learning`"
							class="mb-[16px] font-semibold"
						/>

						<div class="flex flex-col gap-[24px]">
							<template
								v-for="(related_post, index) in relatedNewsItems"
								:key="related_post.slug"
							>
								<AizenRelatedPostAd v-if="index === 1" />

								<template v-if="route.path.includes(related_post.uri)">
									<RelatedPostCard
										:image="fallbackPost.image"
										:title="fallbackPost.title"
										:text="fallbackPost.text"
										:url="fallbackPost.url"
									/>
								</template>

								<RelatedPostCard
									v-else
									:image="related_post.image ? related_post.image : ''"
									:title="related_post.title"
									:isNews="true"
									:text="related_post.content_read_time"
									:url="`/news/${related_post.slug}/`"
								/>
							</template>
						</div>
					</section>
				</template>
				<Divider id="comments-d" />

				<Comments
					:id="String(post.id)"
					:comments="comments"
				/>
			</template>
			<template #aside>
				<!-- CTA Card -->
				<a
					:href="watchlistCtaUrl"
					class="news-cta-card sticky"
					target="_blank"
				>
					<div class="news-cta-card__content">
						<h3 class="news-cta-card__title news-cta-card__title--mobile">
							Click Here for a Millionaire's POV on Trading
							<span class="news-cta-card__ticker">
								{{ primaryStockSymbol || "TICKER" }}
							</span>
						</h3>
						<h3 class="news-cta-card__title news-cta-card__title--desktop">
							Millionaire's
							<br />
							POV on
							<span class="news-cta-card__ticker">
								{{ primaryStockSymbol || "TICKER" }}
							</span>
						</h3>
						<p class="news-cta-card__text">
							Don't miss the next
							<br />
							big breakout.
						</p>
						<span class="news-cta-card__btn">GET POV</span>
					</div>
					<img
						src="/images/ts-ad-cutout.webp"
						alt="Tim Sykes"
						class="news-cta-card__image"
						loading="lazy"
					/>
				</a>

				<!-- Trending Stories (client-only to avoid hydration mismatch) -->
				<ClientOnly>
					<div
						v-if="trendingStories.length"
						class="trending-stories"
					>
						<h3 class="trending-stories__title">
							<span class="trending-stories__title-accent"></span>
							Trending Stories
						</h3>
						<div class="trending-stories__list">
							<div
								v-for="story in trendingStories"
								:key="story.slug"
								class="trending-stories__item"
							>
								<NuxtLink
									:to="`/news/${story.slug}/`"
									class="trending-stories__link"
									v-html="story.title"
								></NuxtLink>
								<div
									v-if="story.date"
									class="trending-stories__date"
								>
									<svg
										width="14"
										height="14"
										viewBox="0 0 24 24"
										fill="none"
									>
										<circle
											cx="12"
											cy="12"
											r="10"
											stroke="#9ca3af"
											stroke-width="2"
										/>
										<path
											d="M12 6v6l4 2"
											stroke="#9ca3af"
											stroke-width="2"
											stroke-linecap="round"
										/>
									</svg>
									{{ formatTrendingDate(story.date) }}
								</div>
							</div>
						</div>
					</div>
				</ClientOnly>
			</template>
		</BlogContainer>
	</div>
</template>

<style lang="scss">
@import "@assets/utils.scss";
@import "@assets/blogPost.scss";
@import "@assets/watchListCTA.scss";

/* ===== News Hero Section ===== */
.news-hero {
	position: relative;
	border-radius: 16px;
	overflow: hidden;
	margin-bottom: 24px;
	aspect-ratio: 16 / 9;
	min-height: unset;

	@media (min-width: 768px) {
		aspect-ratio: unset;
		min-height: 420px;
	}
}

.news-hero__image-wrapper {
	position: absolute;
	inset: 0;
}

.news-hero__image {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.news-hero__image-placeholder {
	width: 100%;
	height: 100%;
	background: #1e293b;
}

.news-hero__gradient {
	position: absolute;
	inset: 0;
	background: linear-gradient(
		to top,
		rgba(0, 0, 0, 0.85) 0%,
		rgba(0, 0, 0, 0.55) 40%,
		rgba(0, 0, 0, 0.15) 70%,
		rgba(0, 0, 0, 0.05) 100%
	);
}

.news-hero__sources {
	position: absolute;
	top: 16px;
	right: 16px;
	display: flex;
	gap: 10px;
	z-index: 2;

	@media (min-width: 768px) {
		top: 24px;
		right: 24px;
	}
}

.news-hero__source-btn {
	display: inline-flex;
	align-items: center;
	gap: 12px;
	padding: 6px 14px;
	background: rgba(255, 255, 255, 0.95);
	border-radius: 12px;
	font-size: 13px;
	font-weight: 600;
	color: #1e293b;
	text-decoration: none;
	transition: background 0.2s;
	backdrop-filter: blur(4px);

	&:hover {
		background: #fff;
	}

	&--stacked {
		flex-direction: column;
		align-items: flex-start;
		gap: 2px;
		padding: 8px 12px;
	}
}

.news-hero__source-label {
	display: hidden;
	background: #3b82f6;
	color: #ffffff;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 0.05em;
	padding: 2px 8px;
	border-radius: 4px;
	line-height: 1.4;
	@include breakpoint(lg) {
		display: inline-block;
	}
}

.news-hero__source-row {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	line-height: 1;
}

.news-hero__source-google {
	font-family: "Product Sans", Arial, sans-serif;
	font-weight: 700;
	font-size: 15px;
	color: #4285f4;
}

.news-hero__content {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 16px;
	z-index: 2;

	@include breakpoint(md) {
		padding: 32px;
	}
}

.news-hero__title {
	font-family: "Fira Sans", sans-serif;
	font-size: 20px;
	font-weight: 700;
	line-height: 1.2;
	color: #ffffff;
	margin: 0 0 12px 0;

	@media (min-width: 768px) {
		font-size: 32px;
		margin-bottom: 16px;
	}

	@media (min-width: 1024px) {
		font-size: 36px;
	}
}

.news-hero__meta {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	font-weight: 600;
	color: rgba(255, 255, 255, 0.85);
	flex-wrap: wrap;

	@media (min-width: 768px) {
		font-size: 13px;
		letter-spacing: 0.05em;
	}
}

.news-hero__author {
	color: rgba(255, 255, 255, 0.85);
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		color: #ffffff;
	}
}

.news-hero__meta-dot {
	color: rgba(255, 255, 255, 0.6);
}

.news-hero__reviewers {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 8px;
	letter-spacing: -0.12px;
	@include breakpoint(lg) {
		gap: 4px 16px;
		letter-spacing: 0;
	}
}

.news-hero__reviewer {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 11px;
	color: rgba(255, 255, 255, 0.75);

	@media (min-width: 768px) {
		font-size: 13px;
	}
}

.news-hero__reviewer-link {
	color: rgba(255, 255, 255, 0.95);
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		color: #ffffff;
	}
}

.news-hero__check-icon {
	flex-shrink: 0;
}

/* ===== Market Data Bar ===== */
/* ===== Sidebar CTA Card ===== */
.news-cta-card {
	background: #1f3a8a;
	border-radius: 16px;
	padding: 20px 20px 20px 24px;
	margin-bottom: 24px;
	position: relative;
	z-index: 2;
	overflow: hidden;
	display: none;
	align-items: center;
	min-height: 100px;
	text-decoration: none;

	@media (min-width: 768px) {
		display: flex;
		padding: 24px;
		margin-bottom: 32px;
		align-items: flex-end;
		min-height: 242px;
	}
}

.news-cta-card--inline-mobile {
	display: flex;
	margin: 20px 0;

	@media (min-width: 768px) {
		display: none;
	}
}

.content .news-cta-card__title--inline {
	display: block;
	font-size: 18px;
	color: #ffffff;
	margin-bottom: 0;
}

.news-cta-card__content {
	position: relative;
	z-index: 2;
	flex: 1 1 auto;
	min-width: 0;
	padding-right: 80px;

	@media (min-width: 768px) {
		flex: 1 1 55%;
		max-width: 212px;
		padding-right: 0;
	}
}

.news-cta-card__title {
	font-family: "Fira Sans", sans-serif;
	font-weight: 800;
	color: #ffffff;
	margin: 0;
	line-height: 1.25;
}

.news-cta-card__title--mobile {
	display: block;
	font-size: 17px;

	@media (min-width: 768px) {
		display: none;
	}
}

.news-cta-card__title--desktop {
	display: none;

	@media (min-width: 768px) {
		display: block;
		font-size: 26px;
		margin-bottom: 10px;
		line-height: 1.2;
	}
}

.news-cta-card__ticker {
	color: #eab308;
}

.news-cta-card__text {
	font-size: 15px;
	line-height: 1.5;
	color: #cbd5e1;
	margin: 0 0 20px 0;
	display: none;

	@media (min-width: 768px) {
		display: block;
	}
}

.news-cta-card__btn {
	display: none;
	align-items: center;
	justify-content: center;
	padding: 12px 28px;
	background: #eab308;
	color: #1a2744;
	font-size: 15px;
	font-weight: 800;
	letter-spacing: 0.04em;
	border-radius: 6px;
	text-decoration: none;
	transition: background 0.2s, transform 0.15s;

	@media (min-width: 768px) {
		display: inline-flex;
	}
}

.news-cta-card:hover .news-cta-card__btn {
	background: #facc15;
	transform: translateY(-1px);
}

.news-cta-card__image {
	position: absolute;
	right: 12px;
	bottom: 0;
	height: 100%;
	width: auto;
	max-height: 90px;
	object-fit: contain;
	object-position: bottom right;
	z-index: 1;
	pointer-events: none;

	@media (min-width: 768px) {
		right: 24px;
		height: auto;
		width: 100%;
		max-width: 142px;
		max-height: none;
	}
}

/* ===== Trending Stories ===== */
.trending-stories {
	margin-bottom: 32px;
}

.trending-stories__title {
	display: flex;
	align-items: center;
	gap: 10px;
	font-family: "Fira Sans", sans-serif;
	font-size: 18px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 20px 0;
}

.trending-stories__title-accent {
	display: block;
	width: 4px;
	height: 22px;
	background: #1a2744;
	border-radius: 2px;
	flex-shrink: 0;
}

.trending-stories__list {
	display: flex;
	flex-direction: column;
	padding: 0 12px;
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
}

.trending-stories__item {
	padding: 12px 0;
	border-bottom: 1px solid $neutral-300;

	&:hover {
		background-color: $primary-50;
	}
	&:first-child {
	}

	&:last-child {
		border-bottom: none;
	}
}

.trending-stories__link {
	display: block;
	font-family: "Fira Sans", sans-serif;
	font-size: 15px;
	font-weight: 600;
	line-height: 1.4;
	color: #111827;
	text-decoration: none;
	margin-bottom: 6px;
	transition: color 0.2s;

	&:hover {
		text-decoration: underline;
	}
}

.trending-stories__date {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	color: #9ca3af;
}
</style>
